/** List page and create / edit form page for any entity (record page lives in RecordPage.tsx). */
import { api } from '../lib/api.ts';
import { useQuery } from '../lib/hooks.ts';
import { useI18n } from '../lib/i18n.tsx';
import { useMeta } from '../lib/meta.tsx';
import { useRoute } from '../lib/router.tsx';
import { EntityList } from '../components/EntityList.tsx';
import { EntityForm, useQueryDefaults } from '../components/Form.tsx';
import { takeProposal, type Proposal } from '../lib/ai.tsx';
import { useMemo, useState } from 'react';
import { PageHead } from '../components/PageHead.tsx';
import { ActionDialog } from '../components/ActionDialog.tsx';
import { ErrorState, Spinner, useToast } from '../components/ui.tsx';
import { Icon } from '../components/Icon.tsx';
import type { ActionSpec, EntityMeta, RecordResult } from '../lib/types.ts';

/** module-level catalogue actions (no record of their own) offered from a list page's head */
const LIST_ACTIONS: Record<string, string[]> = {
  maintenance_order: ['assets.generate_maintenance'],
  material_reservation: ['inventory.reserve'],
  inspection: ['quality.create_inspection'],
  journal_entry: ['finance.post_journal'],
};

export function ListPage({ def }: { def: EntityMeta }) {
  const { page, actions } = useMeta();
  const { path } = useRoute();
  const { locale } = useI18n();
  const nav = page(path);
  const [active, setActive] = useState<ActionSpec | null>(null);
  const acts = useMemo(() => (LIST_ACTIONS[def.name] ?? []).map((c) => actions.find((a) => a.code === c)).filter((a): a is ActionSpec => !!a), [def.name, actions]);
  return (
    <div className="stack">
      <PageHead title={<><Icon name={def.icon ?? nav?.module.icon ?? 'List'} size={22} className="muted" />{def.labelPlural}</>} sub={def.description ?? undefined} crumbs={nav && nav.module.code !== 'home' ? [{ label: nav.module.label, to: nav.module.path }] : []}
        actions={acts.length > 0 && <>{acts.map((a) => <button key={a.code} type="button" className="btn" data-action={a.code} onClick={() => setActive(a)}><Icon name="Sparkles" size={16} /> {locale === 'ar' && a.labelAr ? a.labelAr : a.label}</button>)}</>} />
      <EntityList def={def} />
      {active && <ActionDialog spec={active} open onClose={() => setActive(null)} />}
    </div>
  );
}

export function FormPage({ def, id }: { def: EntityMeta; id?: string }) {
  const { t } = useI18n();
  const { navigate } = useRoute();
  const toast = useToast();
  const queryDefaults = useQueryDefaults();
  const { query } = useRoute();
  // a draft the assistant proposed: its header fields pre-fill the form, its lines are created right after the save
  const proposal = useMemo<Proposal | null>(() => (query.get('ai') === '1' && !id ? takeProposal() : null), [query, id]);
  const defaults = useMemo(() => ({ ...queryDefaults, ...(proposal?.entity === def.name ? proposal.fields ?? {} : {}) }), [queryDefaults, proposal, def.name]);
  const createLines = async (parentId: string) => {
    const lines = proposal?.entity === def.name ? proposal.lines ?? [] : [];
    if (!lines.length) return;
    let n = 0;
    for (const l of lines) {
      const sub = def.subEntities.find((s) => s.entity === l.entity);
      if (!sub) continue;
      try { await api.post(l.entity, { [sub.foreignKey]: parentId, ...l.fields }); n++; } catch (e) { toast.error(e); }
    }
    if (n) toast.success(t('ai.linesCreated', { n }));
  };
  const { data, error, loading, refresh } = useQuery(id ? `rec:${def.name}:${id}` : null, () => api.get<RecordResult>(`${def.name}/${id}`), { ttlMs: 0 });
  if (id && error) return <ErrorState error={error} retry={() => void refresh()} />;
  if (id && (loading || !data)) return <Spinner />;
  const record = data?.record ?? null;
  const title = record ? String(record[def.titleField] ?? '') : '';
  return (
    <div className="stack">
      <PageHead crumbs={[{ label: def.labelPlural, to: def.route }, ...(record ? [{ label: title, to: `${def.route}/${id}` }] : [])]} title={record ? `${t('common.edit')} ${def.label}` : `${t('common.new')} ${def.label}`} />
      <EntityForm def={def} record={record} defaults={defaults} onCancel={() => navigate(record ? `${def.route}/${id}` : def.route)}
        onSaved={async (row, outcome) => { toast.success(outcome === 'pending' ? t('workflow.changePending') : t('common.saved')); if (!id && row.id) await createLines(String(row.id)); navigate(`${def.route}/${row.id ?? id}`); }} />
    </div>
  );
}
